/* eslint-disable */
import React, { useEffect, useState } from 'react';
import Loader from '../common/loader/loader';


const MessageTypes = {
  AUTH_ERROR: 'AUTH_ERROR',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  RENDER_COMPLETE: 'RENDER_COMPLETE',
  ACCOUNT_LOAD_SUCCESS: 'ACCOUNT_LOAD_SUCCESS',
  ACCOUNT_LOAD_FAIL: 'ACCOUNT_LOAD_FAIL',
  MC_SCROLL_TO_TOP: 'MC_SCROLL_TO_TOP',
  CLOSE_FA_MODAL: 'CLOSE_FA_MODAL',
  DASHBOARD_MODAL_STATE: 'DASHBOARD_MODAL_STATE',
  HIDE_MODAL_CLOSE_BUTTON: 'HIDE_MODAL_CLOSE_BUTTON',
} as const;

type MessageTypeValue = typeof MessageTypes[keyof typeof MessageTypes];

const loadScriptTag = (
  url: string,
  id: string,
  target: HTMLElement,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
    target.appendChild(script);
  });

const loadScripts = async (
  urls: string[],
  ids: string[],
  target: HTMLElement,
  onLoad: () => void,
  onError: () => void,
): Promise<void> => {
  try {
    for (let i = 0; i < urls.length; i++) {
      await loadScriptTag(urls[i], ids[i], target);
    }
    onLoad();
  } catch {
    onError();
  }
};

export interface DashboardZoidProps {
  accessToken: string;
  accountNumber: string;
  brand: string;
  country: string;
  language: string;
  externalId: string;
  vin: string;
  email: string;
  /** URL of the hosted zoid component-definition script */
  url: string;
  /** URL of the zoid framework script (must be loaded before the component definition) */
  zoidUrl?: string;
  path?: string;
  source?: string;
  correlationId?: string;
}

const DashboardZoid: React.FC<DashboardZoidProps> = ({
  accessToken,
  accountNumber,
  brand,
  country,
  language: languageCode,
  externalId,
  vin,
  email,
  url,
  zoidUrl = '',
  path = '/',
  source = '',
  correlationId = '',
}) => {
  const [isLoading, setIsLoading] = useState(true);


  const dashboardZoidId = 'dashboard-zoid';
  const zoidFrameworkScriptId = 'zoid-framework';
  const dashboardZoidDefinitionScriptId = 'zoid-definition';

  
  // postMessage handler
  function handleMessage(event: MessageEvent) {
    const messageType: MessageTypeValue = event?.data?.type;
    switch (messageType) {
      case MessageTypes.AUTH_ERROR:
        console.error('myVW Integration ===> Dashboard auth error');
        break;
      case MessageTypes.AUTH_SUCCESS:
        console.info('myVW Integration ===> Dashboard auth success');
        break;
      case MessageTypes.RENDER_COMPLETE:
        console.info('myVW Integration ===> Dashboard render complete');
        break;
      case MessageTypes.ACCOUNT_LOAD_SUCCESS:
        console.info('myVW Integration ===> Dashboard account load success');
        break;
      case MessageTypes.ACCOUNT_LOAD_FAIL:
        console.error('myVW Integration ===> Dashboard account load fail');
        break;
      case MessageTypes.MC_SCROLL_TO_TOP:
        window.scrollTo(0, 0);
        break;
      default:
        break;
    }
  }

  
  // Step 1 – load the zoid component-definition script
  // Step 2 – once loaded, render the component into #dashboard-zoid
  useEffect(() => {
    const onLoadScript = () => setIsLoading(false);
    const onLoadScriptError = () => {
      setIsLoading(false);
      console.error('myVW Integration ===> Error loading zoid dashboard script');
    };
    if (isLoading) {
      const scriptUrls = zoidUrl ? [zoidUrl, url] : [url];
      const scriptIds = zoidUrl
        ? [zoidFrameworkScriptId, dashboardZoidDefinitionScriptId]
        : [dashboardZoidDefinitionScriptId];
      loadScripts(
        scriptUrls,
        scriptIds,
        document.head,
        onLoadScript,
        onLoadScriptError,
      );
    } else {
      const aDashboardComponent = (window as any).DashboardComponent;
      if (aDashboardComponent) {
        console.info(
          `myVW Integration ===> Rendering DashboardComponent | accountNumber:${accountNumber} brand:${brand} country:${country}`,
        );
        aDashboardComponent({
          path,
          accessToken,
          accountNumber,
          source,
          externalId,
          username: email,
          vin,
          brand,
          languageCode,
          country,
          correlationId,
        }).render(`#${dashboardZoidId}`);

        window.addEventListener('message', handleMessage, false);
      } else {
        console.error('myVW Integration ===> window.DashboardComponent is not defined');
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [isLoading]);

  useEffect(() => {
    return () => {
      const defScript = document.getElementById(dashboardZoidDefinitionScriptId);
      if (defScript) defScript.remove();
      const fwScript = document.getElementById(zoidFrameworkScriptId);
      if (fwScript) fwScript.remove();
    };
  }, []);

  return (
    <>
      <Loader isLoading={isLoading} />
      <div
        id={dashboardZoidId}
        style={{
          width: '100vw',
          height: '100vh',
          minHeight: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      />
    </>
  );
};

export default DashboardZoid;
